import React, { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableHeaderProps,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { DateFormat } from 'constants/General'
import { formatAmount } from 'pages/Rewards/utils'
import { BillingSidePanel } from './BillingSidePanel'
import { InvoiceStatus, type Invoice } from './types'
import { getInvoiceStatusTag } from './utils'

export type BillingTableProps = {
  invoices: Invoice[]
  isLoading: boolean
}

export const BillingTable = ({ invoices, isLoading }: BillingTableProps) => {
  const { t } = useTranslation()

  const {
    isSidePanelOpen,
    sidePanelData: selectedInvoice,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(invoices)

  const columns: DataTableHeaderProps[] = [
    { key: 'dateDue', variant: DataTableCellVariants.TEXT, label: t('billing.dateDue') },
    { key: 'type', variant: DataTableCellVariants.TEXT, label: t('billing.type') },
    { key: 'amount', variant: DataTableCellVariants.TEXT, label: t('billing.amount') },
    { key: 'status', variant: DataTableCellVariants.TEXT, label: t('status') },
    {
      key: 'actions',
      variant: DataTableCellVariants.ACTION,
      label: t('billing.action'),
      align: DataTableColumnAlignments.RIGHT,
    },
  ]

  const openSidePanel = useCallback(
    (index: number) => {
      setIsSidePanelOpen(true)
      updateSidePanel(index)
    },
    [setIsSidePanelOpen, updateSidePanel]
  )

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!invoices?.length) return []

    return invoices?.map((invoice, index) => {
      const statusTag = getInvoiceStatusTag(invoice.status, t)

      return {
        id: invoice.id,
        onRowClick: () => openSidePanel(index),
        cells: [
          {
            key: 'dateDue',
            variant: DataTableCellVariants.TEXT,
            Text: dayjs(invoice.dateDue).format(DateFormat.DD_MMM_YYYY),
          },
          {
            key: 'type',
            variant: DataTableCellVariants.TEXT,
            Text: t('billing.invoice'),
          },
          {
            key: 'amount',
            variant: DataTableCellVariants.TEXT,
            Text: formatAmount(invoice.amount),
          },
          {
            key: 'status',
            variant: DataTableCellVariants.HIGHLIGHT,
            Highlights: [
              <Highlight key={`${invoice.id}-status`} type={statusTag.state}>
                {statusTag.label}
              </Highlight>,
            ],
          },
          {
            key: 'actions',
            variant: DataTableCellVariants.ACTION,
            Actions: [
              <DropdownMenu
                key={`context-menu-${invoice.id}`}
                DropdownMenuItems={[
                  <DropdownMenuItem
                    key={`view-${invoice.id}`}
                    LeadingIcon={<Icon name="Info" />}
                    onClick={() => openSidePanel(index)}
                  >
                    {t('billing.viewDetails')}
                  </DropdownMenuItem>,
                  ...(invoice.status === InvoiceStatus.UNPAID
                    ? [
                        <DropdownMenuItem
                          key={`pay-${invoice.id}`}
                          LeadingIcon={<Icon name="CreditCard" />}
                          onClick={() => {}}
                        >
                          {t('billing.payInvoice')}
                        </DropdownMenuItem>,
                      ]
                    : []),
                ]}
              >
                <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
              </DropdownMenu>,
            ],
          },
        ],
      }
    })
  }, [invoices, t, openSidePanel])

  if (isLoading) {
    return (
      <Box>
        <RowsSkeletonLoader rows={10} />
      </Box>
    )
  }

  return (
    <>
      <DataTable headers={columns} data={tableData} />
      <BillingSidePanel
        isOpen={isSidePanelOpen}
        onClose={() => setIsSidePanelOpen(false)}
        paging={paging}
        invoice={selectedInvoice}
      />
    </>
  )
}
